import React, { useState } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    Row,
    Col,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Badge,
    Spinner,
    Alert
} from 'reactstrap';
import { getSignedUrl } from '../api';

const AttachmentsSOA = ({ soaId, token, soaData }) => {
    const [openModal, setOpenModal] = useState(false);
    const [modalContent, setModalContent] = useState({
        url: '',
        fileType: '',
        fileName: '',
        loading: false,
        originalAttachment: null // Store original attachment data for download
    });
    const [downloadingFile, setDownloadingFile] = useState(null);

    const extractS3KeyAndType = (attachmentUrl) => {
        if (!attachmentUrl) return { key: '', fileType: '', fileName: '' };

        if (typeof attachmentUrl === 'string') {
            const urlPattern = /^https:\/\/rentdigicare\.s3\.tor1\.amazonaws\.com\/(.+)$/;
            const match = attachmentUrl.match(urlPattern);
            const key = match ? match[1] : attachmentUrl;
            const fileType = key.split('.').pop()?.toLowerCase() || '';
            const fileName = key.split('/').pop() || `attachment.${fileType}`;
            return { key, fileType, fileName };
        }

        return {
            key: attachmentUrl.url || '',
            fileType: attachmentUrl.fileType?.toLowerCase() || attachmentUrl.url?.split('.').pop()?.toLowerCase() || '',
            fileName: attachmentUrl.fileName || `attachment.${attachmentUrl.fileType || 'file'}`
        };
    };

    // Get file icon based on file type
    const getFileIcon = (fileType) => {
        switch (fileType) {
            case 'pdf':
                return '📄';
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return '🖼️';
            case 'doc':
            case 'docx':
                return '📝';
            case 'xls':
            case 'xlsx':
                return '📊';
            case 'zip':
            case 'rar':
                return '📦';
            default:
                return '📎';
        }
    };

    const handlePreviewAttachment = async (attachmentUrl, index) => {
        try {
            setModalContent(prev => ({ ...prev, loading: true }));
            setOpenModal(true);

            const { key, fileType, fileName } = extractS3KeyAndType(attachmentUrl);

            if (!key) throw new Error('Invalid attachment key');

            const response = await getSignedUrl(key, 'get', token, soaId);

            if (response.success && response.data.signedUrl) {
                setModalContent({
                    url: response.data.signedUrl,
                    fileType,
                    fileName,
                    loading: false,
                    originalAttachment: attachmentUrl // Store original attachment for download
                });
            } else {
                throw new Error('Failed to get signed URL');
            }
        } catch (error) {
            console.error('Error previewing attachment:', error);
            setModalContent(prev => ({ ...prev, loading: false }));
            alert('An error occurred while loading the attachment for preview.');
        }
    };

    // Improved download function with fetch approach
    const downloadFile = async (signedUrl, fileName) => {
        try {
            const response = await fetch(signedUrl);
            if (!response.ok) throw new Error('Failed to fetch file');
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Clean up the blob URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error in downloadFile:', error);
            // Fallback to direct link approach
            const link = document.createElement('a');
            link.href = signedUrl;
            link.download = fileName;
            link.target = '_blank';
            link.style.display = 'none';
            
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    // Handle attachment download
    const handleDownloadAttachment = async (attachmentUrl, index) => {
        try {
            setDownloadingFile(index);

            const { key, fileName } = extractS3KeyAndType(attachmentUrl);

            if (!key) throw new Error('Invalid attachment key');

            const response = await getSignedUrl(key, 'get', token, soaId);

            if (response.success && response.data.signedUrl) {
                await downloadFile(response.data.signedUrl, fileName);
            } else {
                throw new Error('Failed to get signed URL for download');
            }
        } catch (error) {
            console.error('Error downloading attachment:', error);
            alert('An error occurred while downloading the attachment.');
        } finally {
            setDownloadingFile(null);
        }
    };

    // Render file preview content based on file type
    const renderPreviewContent = () => {
        const { url, fileType, loading } = modalContent;

        if (loading) {
            return (
                <div className="text-center p-4">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading preview...</p>
                </div>
            );
        }

        if (!url) {
            return (
                <Alert color="warning">
                    Unable to load preview for this file.
                </Alert>
            );
        }

        switch (fileType) {
            case 'pdf':
                return (
                    <iframe
                        src={url}
                        width="100%"
                        height="500px"
                        title="PDF Preview"
                        style={{ border: 'none' }}
                    />
                );
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return (
                    <div className="text-center">
                        <img
                            src={url}
                            alt="Attachment Preview"
                            style={{ maxWidth: '100%', maxHeight: '500px' }}
                        />
                    </div>
                );
            default:
                return (
                    <div className="text-center p-4">
                        <p>Preview not available for this file type.</p>
                        <Button
                            color="primary"
                            onClick={() => window.open(url, '_blank')}
                        >
                            Open in New Tab
                        </Button>
                    </div>
                );
        }
    };

    const attachments = soaData?.email_details?.attachments || [];

    if (!attachments || attachments.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <h5 className="mb-0">SOA Attachments</h5>
                </CardHeader>
                <CardBody>
                    <Alert color="info">
                        No attachments found for this SOA.
                    </Alert>
                </CardBody>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">SOA Attachments</h5>
                    <Badge color="secondary">{attachments.length} file(s)</Badge>
                </CardHeader>
                <CardBody>
                    <Row>
                        {attachments.map((attachment, index) => {
                            const { fileType, fileName } = extractS3KeyAndType(attachment);
                            const isDownloading = downloadingFile === index;

                            return (
                                <Col md="6" lg="4" key={index} className="mb-3">
                                    <Card className="h-100">
                                        <CardBody className="text-center">
                                            <div className="mb-3">
                                                <span style={{ fontSize: '3rem' }}>
                                                    {getFileIcon(fileType)}
                                                </span>
                                            </div>
                                            <h6 className="card-title text-truncate" title={fileName}>
                                                {fileName}
                                            </h6>
                                            <Badge color="light" className="mb-3">
                                                {fileType.toUpperCase()}
                                            </Badge>
                                            <div className="d-flex gap-2 justify-content-center">
                                                <Button
                                                    color="primary"
                                                    size="sm"
                                                    onClick={() => handlePreviewAttachment(attachment, index)}
                                                >
                                                    👁️ Preview
                                                </Button>
                                                <Button
                                                    color="success"
                                                    size="sm"
                                                    onClick={() => handleDownloadAttachment(attachment, index)}
                                                    disabled={isDownloading}
                                                >
                                                    {isDownloading ? (
                                                        <Spinner size="sm" />
                                                    ) : (
                                                        '⬇️ Download'
                                                    )}
                                                </Button>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </CardBody>
            </Card>

            {/* Preview Modal */}
            <Modal
                isOpen={openModal}
                toggle={() => setOpenModal(false)}
                size="lg"
                className="modal-lg"
            >
                <ModalHeader toggle={() => setOpenModal(false)}>
                    Attachment Preview - {modalContent.fileName}
                </ModalHeader>
                <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {renderPreviewContent()}
                </ModalBody>
                <ModalFooter>
                    <Button
                        color="success"
                        onClick={() => handleDownloadAttachment(modalContent.originalAttachment, -1)}
                        disabled={modalContent.loading || !modalContent.originalAttachment}
                    >
                        Download File
                    </Button>
                    <Button color="secondary" onClick={() => setOpenModal(false)}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export { AttachmentsSOA };